import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, User, Unlock } from 'lucide-react';
import AppNav from '../components/AppNav';
import { apiClient } from '../api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Blocked = () => {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/blocks/me');
      setBlocked(r.data || []);
    } catch (err) {
      console.warn('Failed to load blocks:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unblock = async (id, name) => {
    try {
      await apiClient.delete(`/blocks/${id}`);
      setBlocked((prev) => prev.filter((b) => b.user_id !== id));
      toast.success(`${name || 'User'} unblocked`);
    } catch (err) {
      toast.error('Could not unblock', { description: err.response?.data?.detail || '' });
    }
  };

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-fnl-pink" />
          <h1 className="text-3xl font-extrabold text-white">Blocked users</h1>
        </div>
        <p className="text-purple-300 text-sm mb-6">
          Blocked users can't see your profile or message you, and they're hidden from your browse and views.
          You can also{' '}
          <Link to="/muted" className="text-fnl-pink hover:underline font-semibold">manage muted users</Link>
          {' '}separately (mute only silences notifications).
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : blocked.length === 0 ? (
          <div className="bg-fnl-purple/60 border border-purple-900/40 rounded-2xl p-10 text-center" data-testid="blocked-empty">
            <Shield className="w-12 h-12 text-fnl-pink/40 mx-auto mb-3" />
            <p className="text-white font-semibold">No blocked users</p>
            <p className="text-purple-300 text-sm mt-1">When you block someone, they'll show up here.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="blocked-list">
            {blocked.map((b) => (
              <div key={b.user_id} className="bg-fnl-purple/60 border border-purple-900/40 rounded-xl p-3 flex items-center gap-3" data-testid={`blocked-${b.user_id}`}>
                <div className="w-12 h-12 rounded-full overflow-hidden bg-fnl-pink/20 flex items-center justify-center">
                  {b.photo_base64 ? <img src={b.photo_base64} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-fnl-pink" />}
                </div>
                <div className="flex-1">
                  <div className="text-white font-semibold">{b.name || 'User'}{b.age ? `, ${b.age}` : ''}</div>
                  <div className="text-purple-300 text-xs">Blocked {new Date(b.blocked_at).toLocaleDateString()}</div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => unblock(b.user_id, b.name)}
                  className="bg-fnl-purple-light/40 border-purple-700 text-white hover:bg-fnl-purple-light"
                  data-testid={`unblock-${b.user_id}`}
                >
                  <Unlock className="w-4 h-4 mr-1.5" />Unblock
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blocked;
